// 🚀 ENTERPRISE UPLOAD ROUTER - SINGLE UPLOAD SYSTEM
// Replaces: VideoContext upload methods, VideoUpload logic, legacy services
// Consolidates: Browser-native uploads, multipart uploads, progress tracking, cancellation

import { MultipartUploader } from '../utils/multipartUpload'
import { UPLOAD_CONFIG, getUploadMethod, isValidVideoFile, isFileSizeValid } from '../utils/uploadConstants'
import toast from 'react-hot-toast'

export class UploadRouter {
  constructor() {
    this.activeUpload = null
    this.uploadXhr = null
    this.multipartUploader = null
    this.progressCallback = null
    this.isCancelled = false
  }

  // 🎯 SINGLE UPLOAD ENTRY POINT - replaces all other upload methods
  async upload(file, metadata, onProgress = () => {}) {
    // Reset state
    this.reset()
    this.progressCallback = onProgress
    this.isCancelled = false

    try {
      // Validate file
      const validation = this.validateFile(file)
      if (!validation.isValid) {
        throw new Error(validation.error)
      }

      // Log upload decision
      const method = getUploadMethod(file.size)
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(1)
      console.log(`🚀 UploadRouter: ${method} upload for ${fileSizeMB}MB file`)

      // Route to multipart upload (single path for consistent storage pattern)
      if (method === 'multipart') {
        return await this.multipartUpload(file, metadata)
      } else {
        // This should never happen since getUploadMethod always returns 'multipart'
        throw new Error('Invalid upload method - all uploads should use multipart')
      }

    } catch (error) {
      console.error('❌ UploadRouter: Upload failed:', error)

      // Don't show error toast for user cancellations
      if (error.message === 'Upload cancelled') {
        console.log('📝 UploadRouter: Upload cancelled by user')
        return { cancelled: true }
      }

      // Clean up on error
      this.cleanup()
      throw error
    }
  }

  // 🚫 SINGLE CANCEL METHOD - replaces all other cancel implementations
  cancel() {
    console.log('🚫 UploadRouter: Cancelling upload...')
    this.isCancelled = true

    let cancelMethod = 'none'

    // Cancel multipart upload (primary method - all uploads now use this)
    if (this.multipartUploader) {
      console.log('🚫 UploadRouter: Aborting multipart upload with R2 cleanup')
      this.multipartUploader.cancel()
      this.multipartUploader = null
      cancelMethod = 'multipart-with-r2-cleanup'
    }

    // Legacy XHR cleanup (should not be needed since all uploads are multipart)
    if (this.uploadXhr) {
      console.log('🚫 UploadRouter: Aborting legacy XHR upload (unexpected)')
      this.uploadXhr.abort()
      this.uploadXhr = null
      cancelMethod = 'legacy-xhr-unexpected'
    }

    // Reset progress
    if (this.progressCallback) {
      this.progressCallback(0)
    }

    // 🚀 ENTERPRISE: Clear cancellation confirmation
    console.log(`✅ UploadRouter: Upload cancelled successfully via ${cancelMethod}`)
    toast.success(`Upload cancelled successfully (${cancelMethod})`)

    // Log for verification
    if (cancelMethod === 'multipart-with-r2-cleanup') {
      console.log('🧹 UploadRouter: Multipart upload cancelled - R2 cleanup should have occurred')
      toast.info('Multipart upload cancelled - temporary files cleaned up')
    }
  }


  // 📦 Multipart upload (ALL files - unified upload method)
  async multipartUpload(file, metadata) {
    console.log('📦 UploadRouter: Starting multipart upload')

    // Create multipart uploader
    const uploader = new MultipartUploader(file, (progress) => {
      if (this.progressCallback) {
        this.progressCallback(progress)
      }
    })

    this.multipartUploader = uploader

    // Start multipart upload
    const result = await uploader.start()

    // Extract UID from the R2 key for database storage
    const keyParts = result.key.split('/')
    let uid
    if (keyParts.length > 1) {
      const filename = keyParts[1]
      uid = filename.split('_')[0] // Get timestamp part as UID
    } else {
      uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }

    // Save metadata to database
    await this.saveVideoMetadata(result.key, file, metadata, localStorage.getItem('token'), 'complete-video-multipart')

    console.log('✅ UploadRouter: Multipart upload fully complete')
    this.multipartUploader = null

    return { success: true, location: result.location, key: result.key }
  }

  // 💾 Save video metadata to database (unified for both upload types)
  async saveVideoMetadata(keyOrUid, file, metadata, token, uploadType) {
    const response = await fetch('/.netlify/functions/videos-upload', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        title: metadata.title,
        description: metadata.description,
        category: metadata.category,
        tags: metadata.tags || [],
        visibility: metadata.visibility || 'private',
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type,
        cloudflareUid: keyOrUid.includes('/') ? keyOrUid.split('/')[1].split('_')[0] : keyOrUid,
        uploadType: uploadType
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to save video metadata: ${errorText}`)
    }

    return await response.json()
  }

  // ✅ File validation (unified for all upload types)
  validateFile(file) {
    if (!file) {
      return { isValid: false, error: 'No file selected' }
    }

    if (!isValidVideoFile(file)) {
      return {
        isValid: false,
        error: 'Invalid file type. Please select a video file.'
      }
    }

    if (!isFileSizeValid(file.size)) {
      const maxSizeGB = UPLOAD_CONFIG.MAX_FILE_SIZE_BYTES / (1024 * 1024 * 1024)
      const fileSizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(1)
      return {
        isValid: false,
        error: `File too large (${fileSizeGB}GB). Maximum size is ${maxSizeGB}GB.`
      }
    }

    return { isValid: true }
  }

  // 🧹 Cleanup method
  cleanup() {
    this.uploadXhr = null
    this.multipartUploader = null
    this.isCancelled = false
  }

  // 🔄 Reset method
  reset() {
    this.cleanup()
    this.progressCallback = null
  }

  // 📊 Get upload status
  getStatus() {
    return {
      isUploading: !!this.multipartUploader,
      uploadMethod: this.multipartUploader ? 'multipart' : 'none',
      canCancel: !!this.multipartUploader
    }
  }
}

// 🏭 Singleton instance for application-wide use
export const uploadRouter = new UploadRouter()

// 📋 Enterprise compliance documentation
export const ENTERPRISE_COMPLIANCE = {
  REPLACES_SYSTEMS: [
    'VideoContext.uploadSmallVideo()',
    'VideoContext.uploadLargeVideo()',
    'VideoUpload.handleUpload()',
    'chunkedUploadService.uploadLargeFile()',
    'directUploadService usage'
  ],
  UNIFIES_CANCEL_LOGIC: [
    'VideoContext.cancelUpload()',
    'VideoUpload.handleCancelUpload()',
    'MultipartUploader.cancel()',
    'ChunkedUploadService.cancelUpload()'
  ],
  SINGLE_ENTRY_POINT: 'uploadRouter.upload(file, metadata, onProgress)',
  SINGLE_CANCEL_METHOD: 'uploadRouter.cancel()',
  ELIMINATES_HARDCODED_VALUES: 'All values from uploadConstants.js'
}